import { getDatesArray, getDaysBetweenDates } from 'components/shared/processes'
import {
	MAX_MONTHLY_CHART_POINTS,
	isDateRangeLongerThanMonths,
} from 'components/shared/processes/chartDateAggregation'
import { ICowGraphResponse } from 'models/ICattlesData'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import CattlesApiService from 'services/cattlesApiServices'

const parseDateToTimestamp = (date: string | number | null | undefined) => {
	if (date === null || date === undefined) return null

	if (typeof date === 'number') {
		// Support both seconds and milliseconds timestamps.
		return date < 1e12 ? date * 1000 : date
	}

	const numericDate = Number(date)
	if (!Number.isNaN(numericDate)) {
		return numericDate < 1e12 ? numericDate * 1000 : numericDate
	}

	const parsed = moment(date)
	return parsed.isValid() ? parsed.valueOf() : null
}

const aggregateMonthlyMilkings = (
	graph: { milk_sum: number; date: string }[],
) => {
	const monthlyData: {
		[key: string]: { sum: number; count: number; date_ms: number }
	} = {}

	graph.forEach(entry => {
		const dateTimestamp = parseDateToTimestamp(entry.date)
		if (dateTimestamp === null) return

		const date = moment(dateTimestamp)
		const monthKey = date.format('YYYY-MM')

		if (!monthlyData[monthKey]) {
			monthlyData[monthKey] = {
				sum: 0,
				count: 0,
				date_ms: date.startOf('month').valueOf(),
			}
		}

		if (Number.isFinite(entry.milk_sum)) {
			monthlyData[monthKey].sum += entry.milk_sum
			monthlyData[monthKey].count += 1
		}
	})

	return Object.values(monthlyData)
		.filter(month => month.count > 0)
		.sort((a, b) => a.date_ms - b.date_ms)
		.map(({ sum, count, date_ms }) => ({
			value: Math.round(sum / count),
			date_ms,
		}))
		.slice(-MAX_MONTHLY_CHART_POINTS)
}

const useMilkingsInfoForGraph = cowId => {
	const [milkingInfo, setMilkingInfo] = useState<ICowGraphResponse>()
	const [isLoading, setIsLoading] = useState<boolean>(false)
	const weekDays = getDatesArray(6)
	const [dateRange, setDateRange] = useState({
		startDate: weekDays.startDate,
		endDate: weekDays.endDate,
	})
	const daysDifference = getDaysBetweenDates(dateRange)
	const shouldAggregateMonthly = isDateRangeLongerThanMonths(dateRange, 3)

	const onChangeCallback = value => {
		setMilkingInfo(undefined)
		setIsLoading(true)
		setDateRange({
			startDate: value[0],
			endDate: value[1],
		})
	}

	useEffect(() => {
		if (cowId) {
			setIsLoading(true)
			CattlesApiService.getMilkingGraphByCowId(
				cowId,
				dateRange.startDate,
				dateRange.endDate,
			)
				.then(response => setMilkingInfo(response))
				.finally(() => setIsLoading(false))
		}
	}, [dateRange, cowId])

	const monthlyMilkings = useMemo(
		() => aggregateMonthlyMilkings(milkingInfo?.graph || []),
		[milkingInfo?.graph],
	)
	const categories = shouldAggregateMonthly
		? monthlyMilkings.map(d => d.value)
		: daysDifference !== 0
			? milkingInfo?.graph?.map(el => el.milk_sum)
			: milkingInfo?.graph?.[0]?.milkings.map(el => el.milk_amount)

	const timestamp = shouldAggregateMonthly
		? monthlyMilkings.map(d => d.date_ms)
		: daysDifference !== 0
			? milkingInfo?.graph?.map(el => parseDateToTimestamp(el.date))
			: milkingInfo?.graph?.[0]?.milkings.map(el => el.timestamp)

	return {
		dateRange,
		milkingInfo,
		isLoading,
		categories,
		timestamp,
		onChangeCallback,
		daysDifference,
	}
}

export default useMilkingsInfoForGraph
